import React from 'react';
import { SpaceBetween, Link, Popover, StatusIndicator } from '@amzn/awsui-components-react';
import { ITMPV1_WEBSITE_URL, PERMISSIONS } from 'configuration/config';
import { getString } from 'common/ui-string-labels/ui-strings-utils';
import { UserPermissions } from 'services/user-permissions';

/**
 * Interface representing a related order
 * @interface RelatedOrder
 * @property {string} [id] - Unique identifier for the order
 * @property {string} [orderReference] - Optional reference number/code for the order
 */
export interface RelatedOrder {
  id?: string;
  orderReference?: string;
}

/**
 * Props interface for RelatedOrdersLinks component
 * @interface RelatedOrdersLinksProps
 * @property {any[]} values - Array containing:
 *   - values[0]: [string] - Legacy ID
 *   - values[1]: [string] - First edit status
 *   - values[2]: [RelatedOrder[]] - Array of related orders
 */
export interface RelatedOrdersLinksProps {
  values: [string?, string?, RelatedOrder[]?];
}

/**
 * RelatedOrdersLinks component displays links to related orders and legacy orders
 * @param {RelatedOrdersLinksProps} props - Component props containing values array
 * @returns {JSX.Element} Rendered component with order links
 */
export const RelatedOrdersLinks: React.FC<RelatedOrdersLinksProps> = ({ values }) => {
  // Extract values from props array
  const legacyId = values[0];
  const firstEdit = values[1];
  const relatedOrders = values[2];

  // Check permission to show related order references
  const showRelatedOrders = UserPermissions.instance.hasPermission(PERMISSIONS.SHOW_RELATED_ORDERS);

  // Return N/A if no legacy ID and no related orders to show
  if ((!legacyId || legacyId.length === 0) && (!showRelatedOrders || !relatedOrders || relatedOrders.length === 0)) {
    return <>N/A</>;
  }

  const legacyLink = `${ITMPV1_WEBSITE_URL}procurement/hardware_orders/${legacyId}/edit`;
  const isBlocked = firstEdit && firstEdit !== "ham";

  // Render related orders links if permission exists and orders are available
  const relatedOrdersElement = (showRelatedOrders && Array.isArray(relatedOrders) && relatedOrders.length > 0) ? (
    <SpaceBetween size="xs" direction="horizontal">
      {relatedOrders.map((relation, index) =>
        relation.orderReference && relation.id ? (
          <span key={index}>
            {relation.orderReference}
          </span>
        ) : null
      )}
    </SpaceBetween>
  ) : null;

  // Render legacy order link with blocked status indicator if applicable
  const legacyOrderElement = legacyId ? (
    <SpaceBetween size="xs" direction="horizontal">
      <Link external href={legacyLink}>{legacyId}</Link>
      {isBlocked &&
        <Popover dismissButton={false} position="top" content={getString("common.readOnlyMessage")}>
          <StatusIndicator type="stopped" colorOverride="yellow" />
        </Popover>}
    </SpaceBetween>
  ) : null;

  // Return container with related orders and legacy order elements
  return (
    <SpaceBetween size="xs" direction="horizontal">
      {relatedOrdersElement}
      {legacyOrderElement}
    </SpaceBetween>
  );
};